import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  buildStableContactResearchKeys,
  buildStableLeadResearchKeys,
  findMatchingResearchedContact,
} from '@/utils/researchIdentity';
import type {
  SearchFilters,
  Lead,
  ResearchedContact,
  LinkedInScrapedData,
  SavedSearch,
  RecentSearch,
} from '@/types/research';

interface ResearchState {
  // Search state
  filters: SearchFilters;
  leads: Lead[];
  totalResults: number;
  currentPage: number;
  perPage: number;
  loading: boolean;

  // Cached data
  researchedContacts: Record<string, ResearchedContact>;
  linkedinScrapedData: Record<string, LinkedInScrapedData>;

  // Saved/Recent searches
  savedSearches: SavedSearch[];
  recentSearches: RecentSearch[];
  activeSavedSearchId: string | null;
  pendingSearch: { filters: SearchFilters; page: number; perPage: number } | null;

  // Selection
  selectedLeads: Set<string>;

  // Modals
  linkedinScrapeModalOpen: boolean;
  linkedinScrapeProgress: number;
  linkedinScrapeStatus: string;
  linkedinResultsModalOpen: boolean;
  currentLinkedinData: LinkedInScrapedData | null;
  scheduleModalOpen: boolean;
  saveSearchModalOpen: boolean;
  savedSearchesDrawerOpen: boolean;

  // Actions
  setFilters: (filters: Partial<SearchFilters>) => void;
  resetFilters: () => void;
  setLeads: (leads: Lead[], total: number) => void;
  setCurrentPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setLoading: (loading: boolean) => void;

  saveResearchedContact: (id: string, data: ResearchedContact) => void;
  getResearchedContact: (id: string) => ResearchedContact | null;
  getResearchedContactForLead: (lead: Lead | null | undefined) => ResearchedContact | null;
  saveLinkedinScraped: (url: string, data: LinkedInScrapedData) => void;
  getLinkedinScraped: (url: string) => LinkedInScrapedData | null;

  addSavedSearch: (name: string, filters: SearchFilters) => void;
  setSavedSearches: (searches: SavedSearch[]) => void;
  addRecentSearch: (filters: SearchFilters) => void;
  loadSavedSearch: (index: number, type: 'saved' | 'recent') => SavedSearch | RecentSearch | null;
  setActiveSavedSearchId: (id: string | null) => void;
  updateSavedSearchPage: (id: string, page: number, perPage: number) => void;
  setPendingSearch: (payload: { filters: SearchFilters; page: number; perPage: number } | null) => void;

  toggleLeadSelection: (searchResultId: string) => void;
  selectAllLeads: (selected: boolean) => void;
  getSelectedIndices: () => number[];

  openLinkedinScrapeModal: () => void;
  closeLinkedinScrapeModal: () => void;
  updateLinkedinScrapeProgress: (progress: number, status: string) => void;
  openLinkedinResultsModal: (data: LinkedInScrapedData) => void;
  closeLinkedinResultsModal: () => void;
  openScheduleModal: () => void;
  closeScheduleModal: () => void;
  openSaveSearchModal: () => void;
  closeSaveSearchModal: () => void;
  openSavedSearchesDrawer: () => void;
  closeSavedSearchesDrawer: () => void;
}

const defaultFilters: SearchFilters = {
  names: [],
  companies: [],
  titles: [],
  seniorities: [],
  departments: [],
  industries: [],
  locations: [],
  locationScope: 'both_or',
  locationRadiusMiles: 25,
  employeeSizes: [],
  estimatedRevenues: [],
  keywords: [],
  anyFormerCompanyMatch: true,
  companiesBulkId: [],
  companiesExactMatch: false,
  companyFoundedOn: [],
  companyFundingTotals: [],
  companyLatestFundingClassifications: [],
  companyLatestFundingDates: [],
  companyTypes: [],
  startedCurrentJobDateFrom: '',
  startedCurrentJobDateTo: '',
  titleYearsMin: null,
  titleYearsMax: null,
  skills: [],
  skillsMode: 'or',
  formerCompanies: [],
  formerCompaniesBulkId: [],
  formerCompaniesExactMatch: false,
  includeActivities: false,
  includeCompanyAliases: false,
  includeEducation: false,
  includePositions: false,
  includeTasks: false,
  industryNaicsCodes: [],
  industrySicCodes: [],
  jobChangesDayRange: [],
  jobChangesType: [],
  jobChangesTypeMode: 'or',
  keywordsIsOr: false,
  namesBulkId: [],
  overallSearch: true,
  sortColumn: null,
  sortOrder: null,
  technologies: [],
  technologiesIsOr: true,
  timezones: [],
  timezonesTypes: ['bothAnd'],
  titlesBulkId: [],
  titlesExactMatch: false,
  zipCodes: [],
  zipCodesRadius: [50],
  zipCodesTypes: ['bothAnd'],
  contactSearchId: null,
  nextToken: null,
  page: 0,
};

export const useResearchStore = create<ResearchState>()(
  persist(
    (set, get) => ({
      // Initial state
      filters: { ...defaultFilters },
      leads: [],
      totalResults: 0,
      currentPage: 0,
      perPage: 25,
      loading: false,

      researchedContacts: {},
      linkedinScrapedData: {},

      savedSearches: [],
      recentSearches: [],
      activeSavedSearchId: null,
      pendingSearch: null,

      selectedLeads: new Set(),

      linkedinScrapeModalOpen: false,
      linkedinScrapeProgress: 0,
      linkedinScrapeStatus: 'Initializing...',
      linkedinResultsModalOpen: false,
      currentLinkedinData: null,
      scheduleModalOpen: false,
      saveSearchModalOpen: false,
      savedSearchesDrawerOpen: false,

      // Actions
      setFilters: (filters) =>
        set((state) => ({ filters: { ...state.filters, ...filters } })),

      resetFilters: () => set({ filters: { ...defaultFilters } }),

      setLeads: (leads, total) =>
        set((state) => {
          const next = { ...state.researchedContacts };
          for (const lead of leads) {
            if (!lead.researchedData) continue;
            const keys = new Set<string>([
              ...buildStableLeadResearchKeys(lead),
              ...buildStableContactResearchKeys(lead.researchedData),
            ]);
            for (const key of keys) next[key] = lead.researchedData;
          }
          return { leads, totalResults: total, selectedLeads: new Set(), researchedContacts: next };
        }),

      setCurrentPage: (page) => set({ currentPage: page }),

      setPerPage: (perPage) => set({ perPage }),

      setLoading: (loading) => set({ loading }),

      saveResearchedContact: (id, data) =>
        set((state) => {
          const next = { ...state.researchedContacts };
          const keys = new Set<string>(buildStableContactResearchKeys(data));
          const push = (value: unknown) => {
            const key = String(value ?? '').trim();
            if (key) keys.add(key);
          };
          push(id);
          for (const key of keys) next[key] = data;
          return { researchedContacts: next };
        }),

      getResearchedContact: (id) => get().researchedContacts[id] || null,

      getResearchedContactForLead: (lead) =>
        findMatchingResearchedContact(lead, get().researchedContacts),

      saveLinkedinScraped: (url, data) =>
        set((state) => ({
          linkedinScrapedData: { ...state.linkedinScrapedData, [url]: data },
        })),

      getLinkedinScraped: (url) => get().linkedinScrapedData[url] || null,

      addSavedSearch: (name, filters) =>
        set((state) => {
          const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const newSearches = [
            { id, name, filters, savedAt: new Date().toISOString(), lastPage: 0, perPage: state.perPage },
            ...state.savedSearches,
          ].slice(0, 20);
          return { savedSearches: newSearches };
        }),

      setSavedSearches: (searches) => set({ savedSearches: searches }),

      addRecentSearch: (filters) =>
        set((state) => {
          const str = JSON.stringify(filters);
          const filtered = state.recentSearches.filter(
            (s) => JSON.stringify(s.filters) !== str
          );
          const newSearches = [
            { filters, searchedAt: new Date().toISOString() },
            ...filtered,
          ].slice(0, 10);
          return { recentSearches: newSearches };
        }),

      loadSavedSearch: (index, type) => {
        const state = get();
        const search =
          type === 'saved' ? state.savedSearches[index] : state.recentSearches[index];
        return search ?? null;
      },

      setActiveSavedSearchId: (id) => set({ activeSavedSearchId: id }),

      updateSavedSearchPage: (id, page, perPage) =>
        set((state) => ({
          savedSearches: state.savedSearches.map((s) =>
            s.id === id ? { ...s, lastPage: page, perPage } : s
          ),
        })),

      setPendingSearch: (payload) => set({ pendingSearch: payload }),

      toggleLeadSelection: (searchResultId) =>
        set((state) => {
          const newSet = new Set(state.selectedLeads);
          if (newSet.has(searchResultId)) {
            newSet.delete(searchResultId);
          } else {
            newSet.add(searchResultId);
          }
          return { selectedLeads: newSet };
        }),

      selectAllLeads: (selected) =>
        set((state) => ({
          selectedLeads: selected
            ? new Set(state.leads.map((l) => l.searchResultId))
            : new Set(),
        })),

      getSelectedIndices: () => {
        const state = get();
        return state.leads
          .map((l, i) => (state.selectedLeads.has(l.searchResultId) ? i : -1))
          .filter((i) => i !== -1);
      },

      openLinkedinScrapeModal: () =>
        set({
          linkedinScrapeModalOpen: true,
          linkedinScrapeProgress: 0,
          linkedinScrapeStatus: 'Initializing...',
        }),

      closeLinkedinScrapeModal: () => set({ linkedinScrapeModalOpen: false }),

      updateLinkedinScrapeProgress: (progress, status) =>
        set({ linkedinScrapeProgress: progress, linkedinScrapeStatus: status }),

      openLinkedinResultsModal: (data) =>
        set({ linkedinResultsModalOpen: true, currentLinkedinData: data }),

      closeLinkedinResultsModal: () =>
        set({ linkedinResultsModalOpen: false, currentLinkedinData: null }),

      openScheduleModal: () => set({ scheduleModalOpen: true }),
      closeScheduleModal: () => set({ scheduleModalOpen: false }),

      openSaveSearchModal: () => set({ saveSearchModalOpen: true }),
      closeSaveSearchModal: () => set({ saveSearchModalOpen: false }),

      openSavedSearchesDrawer: () => set({ savedSearchesDrawerOpen: true }),
      closeSavedSearchesDrawer: () => set({ savedSearchesDrawerOpen: false }),
    }),
    {
      name: 'profitibull-research-storage',
      storage: createJSONStorage(() => ({
        getItem: (name: string) => localStorage.getItem(name),
        setItem: (name: string, value: string) => {
          try {
            localStorage.setItem(name, value);
          } catch {
            // If storage is full, drop persisted research state instead of crashing UI.
            try {
              localStorage.removeItem(name);
              localStorage.setItem(name, value);
            } catch {
              // Ignore persistence failure.
            }
          }
        },
        removeItem: (name: string) => localStorage.removeItem(name),
      })),
      partialize: (state) => ({
        // Keep persisted footprint tiny; saved searches are loaded from backend.
        recentSearches: state.recentSearches.slice(0, 5),
        perPage: state.perPage,
      }),
    }
  )
);
